import React, { useEffect, useRef, useState } from "react";
import {
  AvatarQuality,
  StreamingEvents,
  VoiceChatTransport,
  VoiceEmotion,
  STTProvider,
  ElevenLabsModel,
  TaskType,
  TaskMode,
} from "@heygen/streaming-avatar";
import StreamingAvatar from "@heygen/streaming-avatar";
import { useMemoizedFn, useUnmount } from "ahooks";
import { AVATARS, DEFAULT_CONFIG } from "./constants";
import { getApiUrl, config } from "../../utils/api";

const InteractiveAvatar = React.forwardRef(({ onStatusChange, avatarId }, ref) => {
  const [avatar, setAvatar] = useState(null);
  const [sessionState, setSessionState] = useState("INACTIVE");
  const [stream, setStream] = useState(null);
  const [config, setConfig] = useState({
    ...DEFAULT_CONFIG,
    avatarName: avatarId || DEFAULT_CONFIG.avatarName
  });
  const [sessionId, setSessionId] = useState(null); // Store session ID for proper cleanup
  const [error, setError] = useState(null); // Store error messages
  const mediaStream = useRef(null);
  const messageQueue = useRef([]);
  const isProcessing = useRef(false);

  async function fetchAccessToken() {
    try {
      const response = await fetch(getApiUrl('/api/heygen/get-access-token'), {
        method: "POST",
      });
      const token = await response.text();
      console.log("Access Token:", token);
      return token;
    } catch (error) {
      console.error("Error fetching access token:", error);
      throw error;
    }
  }

  // Function to explicitly stop session on backend
  async function stopSessionOnBackend(sessionId) {
    try {
      console.log("🛑 Stopping session on backend:", sessionId);
      const response = await fetch(getApiUrl('/api/session/stop'), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ session_id: sessionId }),
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      console.log("✅ Session stopped on backend successfully");
    } catch (error) {
      console.error("❌ Error stopping session on backend:", error);
      throw error;
    }
  }

  // Function to keep session alive
  async function keepSessionAlive(sessionId) {
    try {
      console.log("💓 Keeping session alive:", sessionId);
      const response = await fetch(getApiUrl('/api/session/keepalive'), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ session_id: sessionId }),
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      console.log("✅ Session keep-alive successful");
    } catch (error) {
      console.error("❌ Error keeping session alive:", error);
      throw error;
    }
  }

  const handleStreamReady = useMemoizedFn((event) => {
    console.log("Stream ready:", event.detail);
    setStream(event.detail);
    setSessionState("CONNECTED");
    onStatusChange?.("connected"); // Notify parent that avatar is connected
  });

  const handleStreamDisconnected = useMemoizedFn(async () => {
    console.log("Stream disconnected");
    // Clean up all event listeners
    if (avatar) {
      avatar.off(StreamingEvents.STREAM_READY, handleStreamReady);
      avatar.off(StreamingEvents.STREAM_DISCONNECTED, handleStreamDisconnected);
      avatar.off(StreamingEvents.AVATAR_START_TALKING);
      avatar.off(StreamingEvents.AVATAR_STOP_TALKING);
      avatar.off(StreamingEvents.AVATAR_END_MESSAGE);
      avatar.off(StreamingEvents.CONNECTION_QUALITY_CHANGED);
      try {
        await avatar.stopAvatar();
      } catch (e) {
        console.warn("Error stopping avatar:", e);
      }
    }
    
    // Explicitly stop session on backend if we have a session ID
    if (sessionId) {
      try {
        await stopSessionOnBackend(sessionId);
      } catch (e) {
        console.warn("Error stopping session on backend:", e);
      }
    }
    
    setAvatar(null);
    setSessionState("INACTIVE");
    setStream(null);
    setSessionId(null); // Clear session ID
    onStatusChange?.("disconnected");
    // Do NOT auto-reconnect here!
  });

  const startSession = useMemoizedFn(async () => {
    try {
      // Don't start if already active
      if (sessionState !== "INACTIVE") {
        console.log("⚠️ Session already active, skipping start");
        return;
      }
      
      // Stop any existing session first to ensure clean state
      if (sessionId) {
        console.log("🧹 Cleaning up existing session before starting new one");
        try {
          await stopSessionOnBackend(sessionId);
        } catch (e) {
          console.warn("Could not clean up existing session:", e);
        }
        setSessionId(null);
      }
      
      console.log("Starting HeyGen avatar session...");
      const token = await fetchAccessToken();
      const avatarInstance = new StreamingAvatar({
        token,
        basePath: config.heygenBaseUrl,
      });
      
      // Set up event listeners
      avatarInstance.on(StreamingEvents.AVATAR_START_TALKING, (e) => {
        console.log("Avatar started talking", e);
        onStatusChange?.("talking");
      });
      avatarInstance.on(StreamingEvents.AVATAR_STOP_TALKING, (e) => {
        console.log("Avatar stopped talking", e);
        onStatusChange?.("idle");
      });
      avatarInstance.on(StreamingEvents.STREAM_READY, handleStreamReady);
      avatarInstance.on(StreamingEvents.STREAM_DISCONNECTED, handleStreamDisconnected);
      
      setAvatar(avatarInstance);
      setSessionState("CONNECTING");
      
      // Create avatar and capture session response
      const startAvatarResponse = await avatarInstance.createStartAvatar(config);
      console.log("[startAvatar] Received startAvatarResponse:", startAvatarResponse);
      
      // Store session_id for proper cleanup
      if (startAvatarResponse && startAvatarResponse.session_id) {
        setSessionId(startAvatarResponse.session_id);
        console.log("📝 Stored session_id:", startAvatarResponse.session_id);
      } else {
        console.warn("⚠️ No session_id found in startAvatarResponse");
      }
      
      console.log("Avatar session started successfully");
      setError(null); // Clear any previous errors
    } catch (error) {
      console.error("Error starting avatar session:", error);
      setSessionState("INACTIVE");
      setSessionId(null);
      
      // Set user-friendly error message
      if (error.message && error.message.includes('400')) {
        setError("API Configuration Error: Please check your HeyGen API key");
      } else if (error.message && error.message.includes('401')) {
        setError("Authentication Error: Invalid HeyGen API key");
      } else {
        setError(`Connection Error: ${error.message || 'Unknown error'}`);
      }
    }
  });

  const stopSession = useMemoizedFn(async () => {
    console.log("🛑 Stopping avatar session...", { avatarExists: !!avatar, sessionId });
    
    // First, stop session on backend if we have a session ID
    if (sessionId) {
      try {
        await stopSessionOnBackend(sessionId);
      } catch (e) {
        console.warn("Error stopping session on backend:", e);
      }
    }
    
    // Then stop the avatar client-side
    if (avatar) {
      try {
        await avatar.stopAvatar();
      } catch (e) {
        console.warn("Error stopping avatar client-side:", e);
      }
    }
    
    // Clean up state
    setAvatar(null);
    setSessionState("INACTIVE");
    setStream(null);
    setSessionId(null);
    console.log("✅ Avatar session stopped completely");
  });

  const processMessageQueue = useMemoizedFn(async () => {
    if (isProcessing.current || messageQueue.current.length === 0) return;
    
    isProcessing.current = true;
    try {
      while (messageQueue.current.length > 0) {
        const message = messageQueue.current[0];
        console.log("📢 Processing queued message:", message);
        
        if (!avatar || sessionState !== "CONNECTED" || !stream) {
          console.warn("Avatar not ready, clearing queue", { 
            avatarExists: !!avatar, 
            sessionState, 
            streamExists: !!stream 
          });
          messageQueue.current = [];
          break;
        }
        
        try {
          await avatar.speak({
            text: message,
            taskType: TaskType.REPEAT,
            taskMode: TaskMode.SYNC, // Use SYNC mode for ordered processing
          });
          console.log("✅ Message processed successfully:", message);
          messageQueue.current.shift(); // Remove processed message
        } catch (error) {
          console.error("❌ Error processing message:", error);
          
          if (error.message && (error.message.includes('401') || error.message.includes('Unauthorized'))) {
            console.log("🔄 401/Unauthorized error detected, restarting avatar session...");
            try {
              await stopSession();
              await new Promise(resolve => setTimeout(resolve, 1500));
              await startSession();
              await new Promise(resolve => setTimeout(resolve, 2000));
              
              if (avatar && sessionState === "CONNECTED") {
                await avatar.speak({
                  text: message,
                  taskType: TaskType.REPEAT,
                  taskMode: TaskMode.SYNC,
                });
                messageQueue.current.shift(); // Remove processed message after successful retry
              }
            } catch (restartError) {
              console.error("❌ Failed to restart session and retry:", restartError);
              messageQueue.current = []; // Clear queue on fatal error
              break;
            }
          } else {
            messageQueue.current.shift(); // Remove failed message
          }
        }
        
        // Add a small delay between messages
        await new Promise(resolve => setTimeout(resolve, 100));
      }
    } finally {
      isProcessing.current = false;
    }
  });

  const sendMessage = useMemoizedFn(async (message) => {
    console.log("🗣️ Queueing message:", message);
    messageQueue.current.push(message);
    processMessageQueue();
  });

  useEffect(() => {
    if (stream && mediaStream.current) {
      mediaStream.current.srcObject = stream;
      mediaStream.current.onloadedmetadata = () => {
        if (mediaStream.current) {
          mediaStream.current.play().catch((e) => {
            console.warn("Video play failed:", e);
          });
        }
      };
    }
  }, [stream]);

  // Auto-start avatar session when component mounts (only once)
  useEffect(() => {
    console.log("🎬 InteractiveAvatar component mounted");
    // Only auto-start if we have a valid API key configured
    // For now, let's disable auto-start to prevent 400/401 errors
    // startSession();
    
    return () => {
      console.log("🎬 InteractiveAvatar component unmounting...");
    };
  }, []); // Empty dependency array to run only once

  // Keepalive mechanism to prevent session timeout
  useEffect(() => {
    if (!sessionId || sessionState !== "CONNECTED") return;
    
    console.log("💓 Setting up keepalive for session:", sessionId);
    const keepaliveInterval = setInterval(async () => {
      if (sessionId && sessionState === "CONNECTED") {
        try {
          await keepSessionAlive(sessionId);
        } catch (error) {
          console.warn("Keepalive failed:", error);
        }
      }
    }, 5 * 60 * 1000); // Keep alive every 5 minutes
    
    return () => {
      console.log("💓 Clearing keepalive interval");
      clearInterval(keepaliveInterval);
    };
  }, [sessionId, sessionState]);

  useUnmount(() => {
    stopSession();
  });

  // Create ref for the start button
  const startButtonRef = useRef(null);

  // Expose methods to parent component using React.forwardRef
  React.useImperativeHandle(ref, () => ({
    startSession,
    stopSession,
    sendMessage,
    sessionState,
    clickStartButton: () => {
      if (startButtonRef.current) {
        startButtonRef.current.click();
      }
    },
  }));

  return (
    <div className="w-full h-full flex flex-col">
      {/* Avatar Video Display */}
      <div className="relative w-full h-64 bg-gray-900 rounded-lg overflow-hidden">
        {sessionState === "CONNECTED" && stream ? (
          <video
            ref={mediaStream}
            width="100%"
            height="100%"
            autoPlay
            className="w-full h-full object-cover"
          />
        ) : sessionState === "CONNECTING" ? (
          <div className="w-full h-full flex items-center justify-center text-white">
            <div className="text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white mx-auto mb-2"></div>
              <p> </p>
            </div>
          </div>
        ) : (
          <div className="w-full h-full flex items-center justify-center text-white">
            <div className="text-center">
              {error ? (
                <div className="mb-4">
                  <p className="text-red-400 mb-2">⚠️ {error}</p>
                  <p className="text-sm text-gray-400 mb-4">
                    Please configure your HeyGen API key in the server environment
                  </p>
                </div>
              ) : (
                <p className="mb-4"> </p>
              )}
              <button
                ref={startButtonRef}
                onClick={() => {
                  setError(null);
                  startSession();
                }}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg text-white font-medium transition opacity-0 pointer-events-none"
                disabled={sessionState === "CONNECTING"}
                style={{ display: 'none' }}
              >
                {sessionState === "CONNECTING" ? "Starting..." : "Start Avatar"}
              </button>
              {sessionId && (
                <button
                  onClick={stopSession}
                  className="ml-2 px-4 py-2 bg-red-600 hover:bg-red-700 rounded-lg text-white font-medium transition"
                >
                  Stop Avatar
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
});

InteractiveAvatar.displayName = 'InteractiveAvatar';

export default InteractiveAvatar; 
// import React, { useEffect, useRef, useState } from "react";
// import {
//   AvatarQuality,
//   StreamingEvents,
//   VoiceChatTransport,
//   VoiceEmotion,
//   STTProvider,
//   ElevenLabsModel,
//   TaskType,
//   TaskMode,
// } from "@heygen/streaming-avatar";
// import StreamingAvatar from "@heygen/streaming-avatar";
// import { useMemoizedFn, useUnmount } from "ahooks";
// import { AVATARS } from "./constants";
// import { getApiUrl, config } from "../../utils/api";

// const DEFAULT_CONFIG = {
//   quality: AvatarQuality.Low,
//   avatarName: AVATARS[5].avatar_id,
//   knowledgeId: undefined,
//   voice: {
//     rate: 1.5,
//     emotion: VoiceEmotion.EXCITED,
//     model: ElevenLabsModel.eleven_flash_v2_5,
//   },
//   language: "en",
//   voiceChatTransport: VoiceChatTransport.WEBSOCKET,
//   sttSettings: {
//     provider: STTProvider.DEEPGRAM,
//   },
// };

// const InteractiveAvatar = React.forwardRef(({ onStatusChange, avatarId }, ref) => {
//   const [avatar, setAvatar] = useState(null);
//   const [sessionState, setSessionState] = useState("INACTIVE");
//   const [stream, setStream] = useState(null);
//   const [config, setConfig] = useState({
//     ...DEFAULT_CONFIG,
//     avatarName: avatarId || DEFAULT_CONFIG.avatarName
//   });
//   const [sessionId, setSessionId] = useState(null); // Store session ID for proper cleanup
//   const [error, setError] = useState(null); // Store error messages
//   const mediaStream = useRef(null);
//   const messageQueue = useRef([]);
//   const isProcessing = useRef(false);

//   async function fetchAccessToken() {
//     try {
//       const response = await fetch(getApiUrl('/api/heygen/get-access-token'), {
//         method: "POST",
//       });
//       const token = await response.text();
//       console.log("Access Token:", token);
//       return token;
//     } catch (error) {
//       console.error("Error fetching access token:", error);
//       throw error;
//     }
//   }

//   // Function to explicitly stop session on backend
//   async function stopSessionOnBackend(sessionId) {
//     try {
//       console.log("🛑 Stopping session on backend:", sessionId);
//       const response = await fetch(getApiUrl('/api/session/stop'), {
//         method: "POST",
//         headers: {
//           "Content-Type": "application/json",
//         },
//         body: JSON.stringify({ session_id: sessionId }),
//       });
      
//       if (!response.ok) {
//         throw new Error(`HTTP error! status: ${response.status}`);
//       }
      
//       console.log("✅ Session stopped on backend successfully");
//     } catch (error) {
//       console.error("❌ Error stopping session on backend:", error);
//       throw error;
//     }
//   }

//   // Function to keep session alive
//   async function keepSessionAlive(sessionId) {
//     try {
//       console.log("💓 Keeping session alive:", sessionId);
//       const response = await fetch(getApiUrl('/api/session/keepalive'), {
//         method: "POST",
//         headers: {
//           "Content-Type": "application/json",
//         },
//         body: JSON.stringify({ session_id: sessionId }),
//       });
      
//       if (!response.ok) {
//         throw new Error(`HTTP error! status: ${response.status}`);
//       }
      
//       console.log("✅ Session keep-alive successful");
//     } catch (error) {
//       console.error("❌ Error keeping session alive:", error);
//       throw error;
//     }
//   }

//   const handleStreamReady = useMemoizedFn((event) => {
//     console.log("Stream ready:", event.detail);
//     setStream(event.detail);
//     setSessionState("CONNECTED");
//     onStatusChange?.("connected"); // Notify parent that avatar is connected
//   });

//   const handleStreamDisconnected = useMemoizedFn(async () => {
//     console.log("Stream disconnected");
//     // Clean up all event listeners
//     if (avatar) {
//       avatar.off(StreamingEvents.STREAM_READY, handleStreamReady);
//       avatar.off(StreamingEvents.STREAM_DISCONNECTED, handleStreamDisconnected);
//       avatar.off(StreamingEvents.AVATAR_START_TALKING);
//       avatar.off(StreamingEvents.AVATAR_STOP_TALKING);
//       avatar.off(StreamingEvents.AVATAR_END_MESSAGE);
//       avatar.off(StreamingEvents.CONNECTION_QUALITY_CHANGED);
//       try {
//         await avatar.stopAvatar();
//       } catch (e) {
//         console.warn("Error stopping avatar:", e);
//       }
//     }
    
//     // Explicitly stop session on backend if we have a session ID
//     if (sessionId) {
//       try {
//         await stopSessionOnBackend(sessionId);
//       } catch (e) {
//         console.warn("Error stopping session on backend:", e);
//       }
//     }
    
//     setAvatar(null);
//     setSessionState("INACTIVE");
//     setStream(null);
//     setSessionId(null); // Clear session ID
//     onStatusChange?.("disconnected");
//     // Do NOT auto-reconnect here!
//   });

//   const startSession = useMemoizedFn(async () => {
//     try {
//       // Don't start if already active
//       if (sessionState !== "INACTIVE") {
//         console.log("⚠️ Session already active, skipping start");
//         return;
//       }
      
//       // Stop any existing session first to ensure clean state
//       if (sessionId) {
//         console.log("🧹 Cleaning up existing session before starting new one");
//         try {
//           await stopSessionOnBackend(sessionId);
//         } catch (e) {
//           console.warn("Could not clean up existing session:", e);
//         }
//         setSessionId(null);
//       }
      
//       console.log("Starting HeyGen avatar session...");
//       const token = await fetchAccessToken();
//       const avatarInstance = new StreamingAvatar({
//         token,
//         basePath: config.heygenBaseUrl,
//       });
      
//       // Set up event listeners
//       avatarInstance.on(StreamingEvents.AVATAR_START_TALKING, (e) => {
//         console.log("Avatar started talking", e);
//         onStatusChange?.("talking");
//       });
//       avatarInstance.on(StreamingEvents.AVATAR_STOP_TALKING, (e) => {
//         console.log("Avatar stopped talking", e);
//         onStatusChange?.("idle");
//       });
//       avatarInstance.on(StreamingEvents.STREAM_READY, handleStreamReady);
//       avatarInstance.on(StreamingEvents.STREAM_DISCONNECTED, handleStreamDisconnected);
      
//       setAvatar(avatarInstance);
//       setSessionState("CONNECTING");
      
//       // Create avatar and capture session response
//       const startAvatarResponse = await avatarInstance.createStartAvatar(config);
//       console.log("[startAvatar] Received startAvatarResponse:", startAvatarResponse);
      
//       // Store session_id for proper cleanup
//       if (startAvatarResponse && startAvatarResponse.session_id) {
//         setSessionId(startAvatarResponse.session_id);
//         console.log("📝 Stored session_id:", startAvatarResponse.session_id);
//       } else {
//         console.warn("⚠️ No session_id found in startAvatarResponse");
//       }
      
//       console.log("Avatar session started successfully");
//       setError(null); // Clear any previous errors
//     } catch (error) {
//       console.error("Error starting avatar session:", error);
//       setSessionState("INACTIVE");
//       setSessionId(null);
      
//       // Set user-friendly error message
//       if (error.message && error.message.includes('400')) {
//         setError("API Configuration Error: Please check your HeyGen API key");
//       } else if (error.message && error.message.includes('401')) {
//         setError("Authentication Error: Invalid HeyGen API key");
//       } else {
//         setError(`Connection Error: ${error.message || 'Unknown error'}`);
//       }
//     }
//   });

//   const stopSession = useMemoizedFn(async () => {
//     console.log("🛑 Stopping avatar session...", { avatarExists: !!avatar, sessionId });
    
//     // First, stop session on backend if we have a session ID
//     if (sessionId) {
//       try {
//         await stopSessionOnBackend(sessionId);
//       } catch (e) {
//         console.warn("Error stopping session on backend:", e);
//       }
//     }
    
//     // Then stop the avatar client-side
//     if (avatar) {
//       try {
//         await avatar.stopAvatar();
//       } catch (e) {
//         console.warn("Error stopping avatar client-side:", e);
//       }
//     }
    
//     // Clean up state
//     setAvatar(null);
//     setSessionState("INACTIVE");
//     setStream(null);
//     setSessionId(null);
//     console.log("✅ Avatar session stopped completely");
//   });

//   const processMessageQueue = useMemoizedFn(async () => {
//     if (isProcessing.current || messageQueue.current.length === 0) return;
    
//     isProcessing.current = true;
//     try {
//       while (messageQueue.current.length > 0) {
//         const message = messageQueue.current[0];
//         console.log("📢 Processing queued message:", message);
        
//         if (!avatar || sessionState !== "CONNECTED" || !stream) {
//           console.warn("Avatar not ready, clearing queue", { 
//             avatarExists: !!avatar, 
//             sessionState, 
//             streamExists: !!stream 
//           });
//           messageQueue.current = [];
//           break;
//         }
        
//         try {
//           await avatar.speak({
//             text: message,
//             taskType: TaskType.REPEAT,
//             taskMode: TaskMode.SYNC, // Use SYNC mode for ordered processing
//           });
//           console.log("✅ Message processed successfully:", message);
//           messageQueue.current.shift(); // Remove processed message
//         } catch (error) {
//           console.error("❌ Error processing message:", error);
          
//           if (error.message && (error.message.includes('401') || error.message.includes('Unauthorized'))) {
//             console.log("🔄 401/Unauthorized error detected, restarting avatar session...");
//             try {
//               await stopSession();
//               await new Promise(resolve => setTimeout(resolve, 1500));
//               await startSession();
//               await new Promise(resolve => setTimeout(resolve, 2000));
              
//               if (avatar && sessionState === "CONNECTED") {
//                 await avatar.speak({
//                   text: message,
//                   taskType: TaskType.REPEAT,
//                   taskMode: TaskMode.SYNC,
//                 });
//                 messageQueue.current.shift(); // Remove processed message after successful retry
//               }
//             } catch (restartError) {
//               console.error("❌ Failed to restart session and retry:", restartError);
//               messageQueue.current = []; // Clear queue on fatal error
//               break;
//             }
//           } else {
//             messageQueue.current.shift(); // Remove failed message
//           }
//         }
        
//         // Add a small delay between messages
//         await new Promise(resolve => setTimeout(resolve, 100));
//       }
//     } finally {
//       isProcessing.current = false;
//     }
//   });

//   const sendMessage = useMemoizedFn(async (message) => {
//     console.log("🗣️ Queueing message:", message);
//     messageQueue.current.push(message);
//     processMessageQueue();
//   });

//   useEffect(() => {
//     if (stream && mediaStream.current) {
//       mediaStream.current.srcObject = stream;
//       mediaStream.current.onloadedmetadata = () => {
//         if (mediaStream.current) {
//           mediaStream.current.play().catch((e) => {
//             console.warn("Video play failed:", e);
//           });
//         }
//       };
//     }
//   }, [stream]);

//   // Auto-start avatar session when component mounts (only once)
//   useEffect(() => {
//     console.log("🎬 InteractiveAvatar component mounted");
//     // Only auto-start if we have a valid API key configured
//     // For now, let's disable auto-start to prevent 400/401 errors
//     // startSession();
    
//     return () => {
//       console.log("🎬 InteractiveAvatar component unmounting...");
//     };
//   }, []); // Empty dependency array to run only once

//   // Keepalive mechanism to prevent session timeout
//   useEffect(() => {
//     if (!sessionId || sessionState !== "CONNECTED") return;
    
//     console.log("💓 Setting up keepalive for session:", sessionId);
//     const keepaliveInterval = setInterval(async () => {
//       if (sessionId && sessionState === "CONNECTED") {
//         try {
//           await keepSessionAlive(sessionId);
//         } catch (error) {
//           console.warn("Keepalive failed:", error);
//         }
//       }
//     }, 5 * 60 * 1000); // Keep alive every 5 minutes
    
//     return () => {
//       console.log("💓 Clearing keepalive interval");
//       clearInterval(keepaliveInterval);
//     };
//   }, [sessionId, sessionState]);

//   useUnmount(() => {
//     stopSession();
//   });

//   // Create ref for the start button
//   const startButtonRef = useRef(null);

//   // Expose methods to parent component using React.forwardRef
//   React.useImperativeHandle(ref, () => ({
//     startSession,
//     stopSession,
//     sendMessage,
//     sessionState,
//     clickStartButton: () => {
//       if (startButtonRef.current) {
//         startButtonRef.current.click();
//       }
//     },
//   }));

//   return (
//     <div className="w-full h-full flex flex-col">
//       {/* Avatar Video Display */}
//       <div className="relative w-full h-64 bg-gray-900 rounded-lg overflow-hidden">
//         {sessionState === "CONNECTED" && stream ? (
//           <video
//             ref={mediaStream}
//             width="100%"
//             height="100%"
//             autoPlay
//             className="w-full h-full object-cover"
//           />
//         ) : sessionState === "CONNECTING" ? (
//           <div className="w-full h-full flex items-center justify-center text-white">
//             <div className="text-center">
//               <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white mx-auto mb-2"></div>
//               <p> </p>
//             </div>
//           </div>
//         ) : (
//           <div className="w-full h-full flex items-center justify-center text-white">
//             <div className="text-center">
//               {error ? (
//                 <div className="mb-4">
//                   <p className="text-red-400 mb-2">⚠️ {error}</p>
//                   <p className="text-sm text-gray-400 mb-4">
//                     Please configure your HeyGen API key in the server environment
//                   </p>
//                 </div>
//               ) : (
//                 <p className="mb-4"> </p>
//               )}
//               <button
//                 ref={startButtonRef}
//                 onClick={() => {
//                   setError(null);
//                   startSession();
//                 }}
//                 className="px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg text-white font-medium transition opacity-0 pointer-events-none"
//                 disabled={sessionState === "CONNECTING"}
//                 style={{ display: 'none' }}
//               >
//                 {sessionState === "CONNECTING" ? "Starting..." : "Start Avatar"}
//               </button>
//               {sessionId && (
//                 <button
//                   onClick={stopSession}
//                   className="ml-2 px-4 py-2 bg-red-600 hover:bg-red-700 rounded-lg text-white font-medium transition"
//                 >
//                   Stop Avatar
//                 </button>
//               )}
//             </div>
//           </div>
//         )}
//       </div>
//     </div>
//   );
// });

// InteractiveAvatar.displayName = 'InteractiveAvatar';

// export default InteractiveAvatar; 
